import React from "react";

import _ from "lodash";

import useForm from "commons/hooks/useForm";

import { FormHookType } from "commons/types/form";
import { ObjectPropertyType } from "commons/types/validation";

import { useBlockGroupContext } from "components/form/Blocks";
import { AttachmentType } from "components/ui/AttachmentPicker";

import { useSaveActionsContext } from "context/save";

import useSectionFiveImagesFifteenTextsApi from "hooks/api/private/blocks/useSectionFiveImagesFifteenTextsApi";
import useMobileOrDesktopFile from "hooks/useMobileOrDesktopFile";
import { ButtonDeleteInterface } from "hooks/useTitleFunctionsTopBlock";

import {
    SectionFiveImagesFifteenTextsBody,
    SectionFiveImagesFifteenTextsDetails,
} from "types/api/blocks/sectionFiveImagesFifteenTexts";
import { DesktopMobileForm, LanguageEnum } from "types/api/commons";
import { DesktopOrMobile } from "types/enums";

import { converterAttachmentToFileWithPayload, createAttachmentType } from "utils/utils";
import { getExtraErrorsDescription, notEmptyMedia } from "utils/validators";

export type SectionThreeParagraphsForm = {
    id: string | undefined;
    img_one_id: string | undefined;
    img_one: DesktopMobileForm;
    text: {
        [x in LanguageEnum]: {
            text_1: string;
            text_2: string;
            text_3: string;
            text_4: string;
            text_5: string;
            text_6: string;
        };
    };
};

type SectionThreeParagraphsType = {
    buttonDelete: ButtonDeleteInterface;
    setImageOne: (fileToSet: AttachmentType, target: DesktopOrMobile) => void;
    formHook: FormHookType<SectionThreeParagraphsForm>;
};

function useSectionThreeParagraphs(): SectionThreeParagraphsType {
    const { blockGroupCompositionId, blockId, graphicalOrder, blockGroupId } =
        useBlockGroupContext();

    const {
        saveSectionFiveImagesFifteenTexts,
        getOneSectionFiveImagesFifteenTexts,
        deleteSectionFiveImagesFifteenTexts,
    } = useSectionFiveImagesFifteenTextsApi();

    const schema = React.useMemo<ObjectPropertyType>(
        () => ({
            type: "object",
            properties: {
                id: {
                    type: "simple",
                    validators: [],
                },
                img_one: notEmptyMedia,
                text: {
                    type: "collection",
                    of: {
                        type: "object",
                        properties: {
                            text_1: {
                                type: "simple",
                                validators: [],
                            },
                            text_2: {
                                type: "simple",
                                validators: [],
                            },
                            text_3: {
                                type: "simple",
                                validators: [],
                            },
                            text_4: {
                                type: "simple",
                                validators: [],
                            },
                            text_5: {
                                type: "simple",
                                validators: [],
                            },
                            text_6: {
                                type: "simple",
                                validators: [],
                            },
                        },
                    },
                },
            },
        }),
        []
    );

    const initialValues: SectionThreeParagraphsForm = React.useMemo(() => {
        return {
            id: undefined,
            img_one_id: undefined,
            img_one: {
                id: undefined,
                desktop: createAttachmentType({ id: null, label: "Desktop" }),
                mobile: createAttachmentType({ id: null, label: "Mobile" }),
            },
            text: {
                it: {
                    text_1: "",
                    text_2: "",
                    text_3: "",
                    text_4: "",
                    text_5: "",
                    text_6: "",
                },
                en: {
                    text_1: "",
                    text_2: "",
                    text_3: "",
                    text_4: "",
                    text_5: "",
                    text_6: "",
                },
            },
        };
    }, []);

    const formHook = useForm<SectionThreeParagraphsForm>(
        schema,
        initialValues,
        getExtraErrorsDescription
    );

    const { showAllError, setForm, isValid, form } = formHook;

    const [sectionFiveImagesFifteenTextsDetails, setSectionFiveImagesFifteenTextsDetails] =
        React.useState<SectionFiveImagesFifteenTextsDetails | undefined>(undefined);

    React.useEffect(() => {
        const [apiCall, abortController] = getOneSectionFiveImagesFifteenTexts(
            { id: blockGroupCompositionId },
            { rejectNotFound: true }
        );

        apiCall.then(setSectionFiveImagesFifteenTextsDetails).catch((error) => console.log(error));

        return () => abortController.abort();
    }, [
        setSectionFiveImagesFifteenTextsDetails,
        getOneSectionFiveImagesFifteenTexts,
        blockGroupCompositionId,
    ]);

    React.useEffect(() => {
        if (sectionFiveImagesFifteenTextsDetails) {
            setForm((prevForm) => {
                const newForm = _.cloneDeep(prevForm);

                newForm.id = sectionFiveImagesFifteenTextsDetails.id;
                newForm.img_one_id = sectionFiveImagesFifteenTextsDetails.img_one_id;
                newForm.img_one = {
                    id: sectionFiveImagesFifteenTextsDetails.img_one?.id,
                    desktop: createAttachmentType({
                        id: sectionFiveImagesFifteenTextsDetails.img_one?.desktop.id,
                        label: "Desktop",
                        filename: sectionFiveImagesFifteenTextsDetails.img_one?.desktop.filename,
                        status: 2,
                    }),
                    mobile: createAttachmentType({
                        id: sectionFiveImagesFifteenTextsDetails.img_one?.mobile.id,
                        label: "Mobile",
                        filename: sectionFiveImagesFifteenTextsDetails.img_one?.mobile.filename,
                        status: 2,
                    }),
                };

                newForm.text.en.text_1 = sectionFiveImagesFifteenTextsDetails.text.en.text_1;
                newForm.text.en.text_2 = sectionFiveImagesFifteenTextsDetails.text.en.text_2;
                newForm.text.en.text_3 = sectionFiveImagesFifteenTextsDetails.text.en.text_3;
                newForm.text.en.text_4 = sectionFiveImagesFifteenTextsDetails.text.en.text_4;
                newForm.text.en.text_5 = sectionFiveImagesFifteenTextsDetails.text.en.text_5;
                newForm.text.en.text_6 = sectionFiveImagesFifteenTextsDetails.text.en.text_6;

                newForm.text.it.text_1 = sectionFiveImagesFifteenTextsDetails.text.it.text_1;
                newForm.text.it.text_2 = sectionFiveImagesFifteenTextsDetails.text.it.text_2;
                newForm.text.it.text_3 = sectionFiveImagesFifteenTextsDetails.text.it.text_3;
                newForm.text.it.text_4 = sectionFiveImagesFifteenTextsDetails.text.it.text_4;
                newForm.text.it.text_5 = sectionFiveImagesFifteenTextsDetails.text.it.text_5;
                newForm.text.it.text_6 = sectionFiveImagesFifteenTextsDetails.text.it.text_6;

                return newForm;
            });
        }
    }, [setForm, sectionFiveImagesFifteenTextsDetails]);

    const { addSaveAction } = useSaveActionsContext();

    React.useEffect(() => {
        addSaveAction(blockGroupCompositionId, () => {
            if (isValid) {
                const section3Images8TextsBody: SectionFiveImagesFifteenTextsBody = {
                    id: blockGroupCompositionId,
                    img_one: {
                        id: form.img_one.id,
                        desktop: converterAttachmentToFileWithPayload(form.img_one.desktop),
                        mobile: converterAttachmentToFileWithPayload(form.img_one.mobile),
                    },
                    img_two: {
                        id: undefined,
                        desktop: undefined,
                        mobile: undefined,
                    },
                    img_three: {
                        id: undefined,
                        desktop: undefined,
                        mobile: undefined,
                    },
                    img_four: {
                        id: undefined,
                        desktop: undefined,
                        mobile: undefined,
                    },
                    img_five: {
                        id: undefined,
                        desktop: undefined,
                        mobile: undefined,
                    },
                    text: {
                        it: {
                            text_1: form.text.it.text_1,
                            text_2: form.text.it.text_2,
                            text_3: form.text.it.text_3,
                            text_4: form.text.it.text_4,
                            text_5: form.text.it.text_5,
                            text_6: form.text.it.text_6,
                            text_7: "",
                            text_8: "",
                            text_9: "",
                            text_10: "",
                            text_11: "",
                            text_12: "",
                            text_13: "",
                            text_14: "",
                            text_15: "",
                        },
                        en: {
                            text_1: form.text.en.text_1,
                            text_2: form.text.en.text_2,
                            text_3: form.text.en.text_3,
                            text_4: form.text.en.text_4,
                            text_5: form.text.en.text_5,
                            text_6: form.text.en.text_6,
                            text_7: "",
                            text_8: "",
                            text_9: "",
                            text_10: "",
                            text_11: "",
                            text_12: "",
                            text_13: "",
                            text_14: "",
                            text_15: "",
                        },
                    },
                };

                const [apiCall] = saveSectionFiveImagesFifteenTexts(section3Images8TextsBody);

                const promises: Promise<unknown>[] = [];

                promises.push(apiCall);

                return Promise.all(promises);
            } else {
                showAllError();
                return Promise.reject("INVALID_FORM");
            }
        });
    }, [
        saveSectionFiveImagesFifteenTexts,
        addSaveAction,
        showAllError,
        blockGroupCompositionId,
        isValid,
        form,
        blockGroupId,
        blockId,
        graphicalOrder,
    ]);

    const { setDesktopMobileImage: setImageOne } = useMobileOrDesktopFile({
        setForm: setForm,
        path: "img_one",
    });

    return React.useMemo(
        (): SectionThreeParagraphsType => ({
            buttonDelete: deleteSectionFiveImagesFifteenTexts,
            setImageOne,
            formHook,
        }),
        [deleteSectionFiveImagesFifteenTexts, setImageOne, formHook]
    );
}

export default useSectionThreeParagraphs;
